import { CircularProgress, IconButton, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { CheckIcon, CloseIcon } from "../../images";
import { useCreateKeyTitleMutation } from "../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../ui/CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { useParams } from "react-router-dom";

const iconStyles = {
  height: "16px",
  width: "16px",
};

const iconButtonStyles = {
  height: "24px",
  width: "24px",
  padding: "0px",
};

type Props = {
  roleId: string;
  setIsOpen: (isOpen: boolean) => void;
  isIgnored: boolean;
  customAddHandler?: (
    title: string,
    group: string,
    onSuccess: () => void,
  ) => void;
  placeholder: string;
  isSubmitting?: boolean;
};

export const KeyTitlesAddItem = ({
  setIsOpen,
  roleId,
  isIgnored,
  customAddHandler,
  placeholder,
  isSubmitting = false,
}: Props) => {
  const { campaign_id } = useParams();
  const [value, setValue] = useState("");

  const [createKeyTitle, { isError, isLoading, isSuccess, error }] =
    useCreateKeyTitleMutation();

  const loading = isLoading || isSubmitting;

  useEffect(() => {
    if (isError && error) {
      toast.error(
        <CustomToast
          type="error"
          text={
            getErrorMessage(error).globalError ||
            Object.values(getErrorMessage(error).fieldErrors)[0] ||
            "Faild to create job title"
          }
          title="Title creation failed"
        />,
      );
    }
  }, [isError]);

  useEffect(() => {
    if (isSuccess) {
      setIsOpen(false);
    }
  }, [isSuccess]);

  const addHandler = () => {
    if (customAddHandler) {
      customAddHandler(value, roleId, () => setIsOpen(false));
      return;
    }

    if (campaign_id) {
      createKeyTitle({
        campaign_id,
        name: value,
        role_id: roleId,
        is_critical: false,
        is_ignored: isIgnored,
      });
    }
  };

  return (
    <Stack flexDirection="row" gap="4px" className="no-drag" maxHeight="32px">
      <Stack
        sx={{
          padding: "4px",
          borderRadius: "6px",
          backgroundColor: isIgnored ? "#F2D7D5" : "#F4F5F7",
          flexDirection: "row",
          alignItems: "center",
          flex: 1,
        }}
      >
        <TextField
          variant="outlined"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          hiddenLabel
          size="small"
          fullWidth
          disabled={loading}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              backgroundColor: "white",
              minHeight: "0",
              border: "1px solid white",

              "&:hover": {
                borderColor: isIgnored ? "#F2D7D5" : "#D9D9D9",
              },
              "&.Mui-focused": {
                borderColor: isIgnored ? "#F2D7D5" : "#D9D9D9",
              },
            },
            "& .MuiOutlinedInput-input": {
              color: "#1F2932",
              fontSize: "14px",
              fontWeight: 500,
            },
          }}
        />

        <Stack flexDirection="row" flexWrap="nowrap" paddingLeft="4px">
          {loading ? (
            <CircularProgress size={16} sx={{ color: "#1D3E70" }} />
          ) : (
            <>
              <IconButton
                sx={iconButtonStyles}
                onClick={() => setIsOpen(false)}
              >
                <CloseIcon {...iconStyles} color="#D32F2F" />
              </IconButton>

              <IconButton sx={iconButtonStyles} onClick={addHandler}>
                <CheckIcon {...iconStyles} color="#27AE60" />
              </IconButton>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
